"use client";

import React, {
  createContext,
  useContext,
  useState,
  ReactNode,
  useCallback,
  useEffect,
} from "react";
import {
  Customer,
  CustomerType,
  CustomerCategory,
  CustomerStatus,
  ServiceType,
} from "@/types";
import { userService } from "@/services/userService";
import { CustomerWithDetails } from "@/types";
import { useActivities } from "./ActivityContext";
import { useAuth } from "./AuthContext";
import { toast } from "sonner";

interface CustomerContextType {
  customers: Customer[];
  loading: boolean;
  error: string | null;
  addCustomer: (
    customer: Omit<
      Customer,
      | "id"
      | "createdAt"
      | "totalSpending"
      | "customerType"
      | "customerCategory"
      | "customerStatus"
      | "numberOfInteriorCleaning"
      | "serviceProviders"
    > &
      Partial<
        Pick<
          Customer,
          | "customerType"
          | "customerCategory"
          | "customerStatus"
          | "numberOfInteriorCleaning"
          | "serviceProviders"
        >
      >
  ) => Customer;
  updateCustomer: (id: string, customer: Partial<Customer>) => void;
  deleteCustomer: (id: string) => void;
  getCustomerById: (id: string) => Customer | undefined;
  fetchCustomers: () => Promise<void>;
  refreshCustomers: () => Promise<void>;
}

const CustomerContext = createContext<CustomerContextType | undefined>(undefined);

// Helper function to transform CustomerWithDetails to Customer
function transformCustomer(apiCustomer: CustomerWithDetails): Customer {
  return {
    id: apiCustomer.id,
    name: apiCustomer.name,
    phone: apiCustomer.phone,
    email: apiCustomer.email || undefined,
    address: apiCustomer.address || undefined,
    city: apiCustomer.city || undefined,
    notes: apiCustomer.notes || undefined,
    createdAt: new Date(apiCustomer.createdAt),
    totalSpending: apiCustomer.totalSpending,
    lastVisit: undefined,
    serviceType: apiCustomer.serviceType || undefined,
    customerType: apiCustomer.customerType,
    customerCategory: apiCustomer.customerCategory,
    customerStatus: apiCustomer.customerStatus,
    cleaningDay: apiCustomer.cleaningDay || undefined,
    cleaningTime: apiCustomer.cleaningTime || undefined,
    numberOfInteriorCleaning: apiCustomer.numberOfInteriorCleaning,
    serviceProviders: apiCustomer.serviceProviders,
    autoRenew: apiCustomer.autoRenew || false,
  };
}

export function CustomerProvider({ children }: { children: ReactNode }) {
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const { addActivity } = useActivities();
  const { isAuthenticated, isLoading: authLoading, user } = useAuth();

  const fetchCustomers = useCallback(async () => {
    // Only fetch if authenticated
    if (!isAuthenticated) {
      return;
    }

    // Only fetch customers if user is ADMIN or manager (staff members don't have permission)
    if (user) {
      const userRole = user.role?.toUpperCase();
      const isAdminOrManager = userRole === 'ADMIN' || userRole === 'MANAGER' || user.role === 'manager';
      
      if (!isAdminOrManager) {
        // For staff members, set empty array (they don't need full customer list)
        setCustomers([]);
        return;
      }
    }

    setLoading(true);
    setError(null);
    try {
      const data = await userService.getAllCustomers();
      const transformed = data.map(transformCustomer);
      transformed.sort((a, b) => a.name.localeCompare(b.name));
      setCustomers(transformed);
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message || error?.message || "Failed to fetch customers";
      setError(errorMessage);
      console.error("Error fetching customers:", error);
      // Only show error toast for admin/manager users, silently fail for others
      if (user) {
        const userRole = user.role?.toUpperCase();
        const isAdminOrManager = userRole === 'ADMIN' || userRole === 'MANAGER' || user.role === 'manager';
        if (isAdminOrManager) {
          toast.error(errorMessage);
        }
      }
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, user]);

  const refreshCustomers = useCallback(async () => {
    await fetchCustomers();
  }, [fetchCustomers]);

  const addCustomer = useCallback(
    (
      customerData: Omit<
        Customer,
        | "id"
        | "createdAt"
        | "totalSpending"
        | "customerType"
        | "customerCategory"
        | "customerStatus"
        | "numberOfInteriorCleaning"
        | "serviceProviders"
      > &
        Partial<
          Pick<
            Customer,
            | "customerType"
            | "customerCategory"
            | "customerStatus"
            | "numberOfInteriorCleaning"
            | "serviceProviders"
          >
        >
    ) => {
      let newCustomer: Customer;
      setCustomers((prev) => {
        newCustomer = {
          ...customerData,
          customerType:
            customerData.customerType || ("offline-customer" as CustomerType),
          customerCategory:
            customerData.customerCategory ||
            ("occasional-customer" as CustomerCategory),
          customerStatus:
            customerData.customerStatus || ("active" as CustomerStatus),
          numberOfInteriorCleaning: customerData.numberOfInteriorCleaning ?? 0,
          serviceProviders: customerData.serviceProviders || [],
          id: `C${String(prev.length + 1).padStart(3, "0")}`,
          createdAt: new Date(),
          totalSpending: 0,
        };
        return [newCustomer, ...prev];
      });
      addActivity({
        userId: "SYSTEM",
        action: "created",
        module: "customer",
        recordId: newCustomer!.id,
        description: `New customer registered: ${newCustomer!.name}`,
      });
      return newCustomer!;
    },
    [addActivity]
  );

  const updateCustomer = useCallback(
    (id: string, customerData: Partial<Customer>) => {
      setCustomers((prev) =>
        prev.map((c) => (c.id === id ? { ...c, ...customerData } : c))
      );
    },
    []
  );

  const deleteCustomer = useCallback((id: string) => {
    setCustomers((prev) => prev.filter((c) => c.id !== id));
  }, []);

  const getCustomerById = useCallback(
    (id: string) => {
      return customers.find((c) => c.id === id);
    },
    [customers]
  );

  // Fetch customers on mount, but only if authenticated and user is admin or manager
  useEffect(() => {
    // Wait for auth to finish loading, then check if authenticated
    if (!authLoading && isAuthenticated) {
      fetchCustomers();
    }
  }, [fetchCustomers, authLoading, isAuthenticated]);

  const value: CustomerContextType = {
    customers,
    loading,
    error,
    addCustomer,
    updateCustomer,
    deleteCustomer,
    getCustomerById,
    fetchCustomers,
    refreshCustomers,
  };

  return <CustomerContext.Provider value={value}>{children}</CustomerContext.Provider>;
}

export function useCustomers() {
  const context = useContext(CustomerContext);
  if (context === undefined) {
    throw new Error("useCustomers must be used within a CustomerProvider");
  }
  return context;
}

